import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';

import { useBesluitId } from '@/api/queries/besluit';
import { useHoofdstukByLineageId } from '@/api/queries/objects/selectors/useHoofdstukByLineageId';
import { useOverwegingByHoofdstukLineageId } from '@/api/queries/overwegingHoofdstuk';
import { Button } from '@/components/Button/Button';
import { Divider } from '@/components/Divider/Divider';
import { ModalTrigger } from '@/components/Modal';
import { DeleteInhoudelijkeOverweging } from '@/modals/DeleteInhoudelijkeOverweging';

import { InhoudelijkeOverweging as InhoudelijkeOverwegingView } from './-components/InhoudelijkeOverweging/InhoudelijkeOverweging';
import { ToolbarContentPage } from './-components/ToolbarContentPage/ToolbarContentPage';
import { Route as VoorschriftenHoofdstukIdRoute } from './voorschriften.$hoofdstukLineageId';

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/inhoudelijke-overwegingen/$hoofdstukLineageId'
)({
  loader: async ({
    params: { hoofdstukLineageId, besluitId },
    context: { queryClient },
  }) => {
    const [hoofdstukResponse] = await Promise.all([
      queryClient.ensureQueryData(
        useHoofdstukByLineageId.getOptions(hoofdstukLineageId)
      ),
      queryClient.ensureQueryData(useBesluitId.getOptions(besluitId)),
      queryClient.ensureQueryData(
        useOverwegingByHoofdstukLineageId.queryOptions({
          besluitId,
          hoofdstukLineageId,
        })
      ),
    ]);

    const hoofdstuk = hoofdstukResponse.objects?.[0];

    if (!hoofdstuk) throw notFound();
  },
  component: InhoudelijkeOverwegingen,
});

/**
 * Route component
 */
function InhoudelijkeOverwegingen() {
  const { hoofdstukLineageId, besluitId, bedrijfLineageId } = Route.useParams();
  const navigate = useNavigate();

  const hoofdstuk = useHoofdstukByLineageId(hoofdstukLineageId);
  const besluit = useBesluitId(besluitId);
  const overweging = useOverwegingByHoofdstukLineageId({
    besluitId,
    hoofdstukLineageId,
  });

  if (!hoofdstuk.data || !besluit.data) {
    return null;
  }

  const onDeleteSuccess = () =>
    navigate({
      to: VoorschriftenHoofdstukIdRoute.fullPath,
      params: { bedrijfLineageId, besluitId, hoofdstukLineageId },
    });

  return (
    <main className="flex min-h-screen flex-col items-center pt-7">
      {overweging.data && (
        <div className="flex w-full max-w-[112rem] justify-end px-6">
          <ModalTrigger
            modalComponent={DeleteInhoudelijkeOverweging}
            onOpen={() => ({
              overwegingId: overweging.data!.ID,
              onSuccess: onDeleteSuccess,
            })}
            asChild
          >
            <Button
              disabled={besluit.data.Status === 'Bevroren'}
              variant="tertiary"
            >
              Inhoudelijke overwegingen verwijderen
            </Button>
          </ModalTrigger>
        </div>
      )}
      <div className="w-full max-w-[112rem] px-6 pb-6 pt-3">
        <div className="px-6 py-1">
          <p className="style-body text-gray-700">{hoofdstuk.data.Nummer}</p>
          <h1
            className="style-h1 text-gray-900"
            dangerouslySetInnerHTML={{ __html: hoofdstuk.data.Titel }}
          />
        </div>

        <Divider className="my-3" />

        {overweging.data && (
          <InhoudelijkeOverwegingView
            menuBarClassName="sticky top-7"
            hoofdstuk={hoofdstuk.data}
            besluit={besluit.data}
            inhoudelijkeOverweging={overweging.data}
          />
        )}
      </div>

      <ToolbarContentPage besluit={besluit.data} />
    </main>
  );
}
